import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  AlertTriangle, 
  Search, 
  Building2, 
  User,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  Eye,
  Trash2,
  RefreshCw,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_STATUSES = ["Nouveau", "En cours", "Vérifié", "Résolu", "Classé"];

const STATUS_CONFIG = {
  "Nouveau": { color: "bg-red-100 text-red-700 border-red-200", icon: AlertTriangle },
  "En cours": { color: "bg-orange-100 text-orange-700 border-orange-200", icon: Clock },
  "Vérifié": { color: "bg-blue-100 text-blue-700 border-blue-200", icon: Eye },
  "Résolu": { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  "Classé": { color: "bg-slate-100 text-slate-700 border-slate-200", icon: FileText }
};

const TYPE_CONFIG = {
  "Injoignable": { color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  "Faillite": { color: "bg-red-100 text-red-700 border-red-200" },
  "Déménagement": { color: "bg-purple-100 text-purple-700 border-purple-200" },
  "Fermeture": { color: "bg-red-100 text-red-700 border-red-200" },
  "Changement de contact": { color: "bg-blue-100 text-blue-700 border-blue-200" },
  "Autre": { color: "bg-slate-100 text-slate-700 border-slate-200" }
};

export const CompanyReports = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/company-reports${params}`),
        axios.get(`${API}/company-reports/stats/summary`)
      ]);
      setReports(reportsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des signalements');
    } finally {
      setLoading(false);
    }
  };

  const openDetailsDialog = (report) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || '');
    setDetailsDialogOpen(true);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedReport) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/company-reports/${selectedReport.id}`, {
        status: newStatus,
        admin_notes: adminNotes
      });
      toast.success('Statut mis à jour');
      setDetailsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedReport) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/company-reports/${selectedReport.id}`, {
        admin_notes: adminNotes
      });
      toast.success('Notes enregistrées');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (reportId) => {
    try {
      await axios.delete(`${API}/company-reports/${reportId}`);
      toast.success('Signalement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReports = reports.filter(report =>
    report.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && reports.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="company-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              Signalements
            </h1>
            <p className="text-slate-500 mt-1">
              Problèmes signalés par les consultants concernant les entreprises
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-sm text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-700">{stats.new_count}</p>
                    <p className="text-sm text-red-600">Nouveaux</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.by_status?.["En cours"] || 0}</p>
                    <p className="text-sm text-slate-500">En cours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.by_status?.["Résolu"] || 0}</p>
                    <p className="text-sm text-slate-500">Résolus</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <XCircle className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.by_status?.["Classé"] || 0}</p>
                    <p className="text-sm text-slate-500">Classés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par entreprise, consultant, description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="w-full sm:w-48">
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    {REPORT_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {filteredReports.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {reports.length === 0 ? 'Aucun signalement' : 'Aucun signalement ne correspond à vos critères'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-slate-500 font-medium">Statut</TableHead>
                      <TableHead className="text-slate-500 font-medium">Entreprise</TableHead>
                      <TableHead className="text-slate-500 font-medium">Type</TableHead>
                      <TableHead className="text-slate-500 font-medium">Consultant</TableHead>
                      <TableHead className="text-slate-500 font-medium">Date</TableHead>
                      <TableHead className="text-slate-500 font-medium text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReports.map((report) => {
                      const StatusIcon = STATUS_CONFIG[report.status]?.icon || AlertTriangle;
                      return (
                        <TableRow key={report.id} data-testid={`report-row-${report.id}`}>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={STATUS_CONFIG[report.status]?.color || 'bg-slate-100'}
                            >
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {report.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="font-medium text-slate-900">{report.company_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={TYPE_CONFIG[report.report_type]?.color || 'bg-slate-100'}
                            >
                              {report.report_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{report.consultant_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-slate-500 text-sm">{formatDate(report.created_at)}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openDetailsDialog(report)}
                                data-testid={`view-report-${report.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Traiter
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Supprimer ce signalement ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Cette action est irréversible.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(report.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Supprimer
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Détails du signalement
              </DialogTitle>
              <DialogDescription>
                Signalement concernant {selectedReport?.company_name}
              </DialogDescription>
            </DialogHeader>
            {selectedReport && (
              <div className="flex-1 overflow-y-auto py-4 space-y-4">
                {/* Status and Type */}
                <div className="flex items-center gap-3">
                  <Badge 
                    variant="outline" 
                    className={STATUS_CONFIG[selectedReport.status]?.color || 'bg-slate-100'}
                  >
                    {selectedReport.status}
                  </Badge>
                  <Badge 
                    variant="outline" 
                    className={TYPE_CONFIG[selectedReport.report_type]?.color || 'bg-slate-100'}
                  >
                    {selectedReport.report_type}
                  </Badge>
                </div>

                {/* Info */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Entreprise concernée</p>
                    <p className="text-slate-900 font-medium">{selectedReport.company_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Signalé par</p>
                    <p className="text-slate-700">{selectedReport.consultant_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Date du signalement</p>
                    <p className="text-slate-700">{formatDate(selectedReport.created_at)}</p>
                  </div>
                </div>

                {/* Description */}
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-xs text-orange-600 uppercase font-medium mb-2">Description du problème</p>
                  <p className="text-slate-800 whitespace-pre-wrap">{selectedReport.description}</p>
                </div>

                {/* Admin Notes */}
                <div className="space-y-2">
                  <Label>Notes administrateur</Label>
                  <Textarea
                    placeholder="Ajoutez des notes sur les actions prises, les vérifications effectuées..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveNotes}
                    disabled={updating}
                  >
                    Enregistrer les notes
                  </Button>
                </div>

                {/* Status Update */}
                <div className="space-y-2">
                  <Label>Changer le statut</Label>
                  <div className="flex flex-wrap gap-2">
                    {REPORT_STATUSES.map(status => {
                      const StatusIcon = STATUS_CONFIG[status]?.icon || AlertTriangle;
                      return (
                        <Button
                          key={status}
                          variant={selectedReport.status === status ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleUpdateStatus(status)}
                          disabled={updating || selectedReport.status === status}
                          className={selectedReport.status === status ? "bg-slate-900" : ""}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="border-t pt-4">
              <Button 
                variant="outline"
                onClick={() => setDetailsDialogOpen(false)}
              >
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
